/*----------------------------------
- PRODUCT FEATURE PAGE LAYOUT
----------------------------------*/

/**
 * The layout of the four feature pages (`/product/search`, `/product/verify`, `/product/radars`,
 * `/product/watchlists`) on the design system, from docs/mockups/website-product.html: the feature tabs, the hero
 * with the persona pair beside the still sample card, an optional figure under it (Search's screener film), How (a
 * tray of numbered cards), What you get, the chip groups, Next (numbered rows, the last one butter), the plan, the
 * questions, the closing band, Continue the workflow, Ask AI and the AI context. Each feature page passes its copy,
 * its trigger ids and its GEO context.
 *
 * @docs docs/features/website-product-feature-pages
 * @adr  ADR-0110
 * @rule Every figure the plan card prints is read from `PUBLIC_PLAN_FACTS` (the price here, the limits in the
 *       catalogs), never typed into copy.
 * @rule The conversion path is frozen: the hero pair (`ProductHeroPair`), the plan card's link to `/pricing#plans`
 *       (section `plan`, the page's `*_plan_pro` trigger) and the closing band's "Start with my search" (section
 *       `final_cta`, the page's `*_final_get_started` trigger, `links.productDomains`) keep their hrefs and events.
 *       The phone action bar that repeated the investor sign-up is gone (2026-10-27); the hero pair shows at every
 *       width, and Log in is in the phone menu, so the closing band adds nothing.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';

import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import type { GeoContext } from '@uniquedomains/components/geo/pageGeo';
import { Button } from '@uniquedomains/components/kit/button/Button';
import { Chip } from '@uniquedomains/components/kit/chip/Chip';
import { Segmented } from '@uniquedomains/components/kit/segmented/Segmented';
import { Tag } from '@uniquedomains/components/kit/tag/Tag';
import { ClosingBand } from '@uniquedomains/components/marketing/closing-band/ClosingBand';
import { MarketingCard } from '@uniquedomains/components/marketing/marketing-page/MarketingCard';
import { PlanCard } from '@uniquedomains/components/marketing/marketing-page/PlanCard';
import { StepList } from '@uniquedomains/components/marketing/marketing-page/StepList';
import { StepNumber } from '@uniquedomains/components/marketing/marketing-page/StepNumber';
import { Tray } from '@uniquedomains/components/marketing/marketing-page/Tray';
import { RelatedPages } from '@uniquedomains/components/marketing/related-pages/RelatedPages';
import { SectionHead } from '@uniquedomains/components/marketing/section-head/SectionHead';
import { PageMain } from '@uniquedomains/components/marketing/section/PageMain';
import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { formatUsd } from '@uniquedomains/components/pricing/utils/plans';

import { MARKETING_ASK_AI_COPY } from '@/client/catalogs/landing/marketingPage';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { PRODUCT_FEATURE_TABS } from '@/client/catalogs/landing/product/featureTabs';
import { PRODUCT_FEATURE_PAGE_CHROME } from '@/client/catalogs/landing/product/pageChrome';
import type { ProductFeatureGroup, ProductFeaturePageContent, ProductFeatureSlug } from '@/client/catalogs/landing/product/types';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { GlossarySection } from '@/client/components/glossary/GlossarySection';
import MarketingGlossaryText from '@/client/pages/landing/components/marketing-page/MarketingGlossaryText';
import Icon from '@/client/components/ui/icon/Icon';
import WebsiteAskAi from '@/client/pages/landing/components/ask-ai/WebsiteAskAi';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import HeroSampleCard from '@/client/pages/landing/components/marketing-page/HeroSampleCard';
import MarketingFaqSection from '@/client/pages/landing/components/marketing-page/MarketingFaqSection';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';
import PersonaPair from '@/client/pages/landing/components/conversion/PersonaPair';

import '../productFeaturePages.css';

/*----------------------------------
- TYPES
----------------------------------*/

type ProductFeaturePageProps = {
    feature: ProductFeatureSlug;
    content: ProductFeaturePageContent;
    navigation?: LandingHeaderDomainsNavigation;
    /** Analytics page id, e.g. "product_search". */
    trackingPage: string;
    /** Analytics CTA surface, e.g. "product_search_page". */
    surface: string;
    currentPath: string;
    /** Signup attribution carried by the hero persona pair. */
    attribution: string;
    investorTriggerId: ConversionTriggerId;
    founderTriggerId: ConversionTriggerId;
    /** Plan card CTA, which opens plan comparison rather than checkout. */
    planTriggerId: ConversionTriggerId;
    finalTriggerId: ConversionTriggerId;
    askAiContext: AskYourAIProps;
    geoContext: GeoContext;
    geoSurface: string;
    /**
     * A full-width figure between the hero and How. Search passes the screener film here, the figure that left Home on
     * 2026-09-27; the other features pass nothing.
     */
    afterHero?: ComponentChildren;
};

type CtaTracking = { section: string; label: string; kind: string; href: string; triggerId: ConversionTriggerId };

/*----------------------------------
- CONSTANTS
----------------------------------*/

const planPrice = formatUsd(PUBLIC_PLAN_FACTS.member.price);

/** A step's number as the mock writes it ("01"), formatting only. */
const stepNumber = (index: number): string => String(index + 1).padStart(2, '0');

/**
 * The chip groups' grid as the mock draws it per feature: four columns for four groups or more (Search's six wrap to a
 * second row), three for Verify's three, two below that.
 */
const groupColumnsClass = (groups: readonly ProductFeatureGroup[]): string =>
    groups.length >= 4 ? 'mk-cols4' : groups.length === 3 ? 'mk-cols3' : 'mk-cols2';

/*----------------------------------
- COMPONENT
----------------------------------*/

const ProductFeaturePage = ({
    feature,
    content,
    navigation = EMPTY_LANDING_NAVIGATION,
    trackingPage,
    surface,
    currentPath,
    attribution,
    investorTriggerId,
    founderTriggerId,
    planTriggerId,
    finalTriggerId,
    askAiContext,
    geoContext,
    geoSurface,
    afterHero,
}: ProductFeaturePageProps) => {
    /*----------------------------------
    - INIT
    ----------------------------------*/

    const links = LANDING_WEBSITE_LINKS;
    const pricingPlansHref = `${links.pricing}#plans`;
    const relatedLinks = getHumanPageRelationships(currentPath).map(({ label, targetPath }) => ({ label, href: targetPath }));

    /*----------------------------------
    - EFFECTS
    ----------------------------------*/

    useEffect(() => {
        track('page_view', { page: trackingPage, path: window.location.pathname + window.location.search });
    }, [trackingPage]);

    /*----------------------------------
    - ACTIONS
    ----------------------------------*/

    const trackCta = ({ section, label, kind, href, triggerId }: CtaTracking) => {
        trackLandingCta({ page: trackingPage, section, label, kind, href, surface });
        trackConversionTrigger(triggerId, { href, section });
    };

    /*----------------------------------
    - RENDER
    ----------------------------------*/

    return (
        <>
            <Header spacer={false} links={links} navigation={navigation} currentPath={currentPath} trackingPage={trackingPage} />

            <PageMain id="top">
                {/*---- - HERO ----*/}
                <section class="wrap pf-hero">
                    {/* Four real routes, so the tabs are four links: the active page marks itself, no feature needs
                        client state, and a crawler reads the siblings from here. The landmark is a `nav`, as shipped;
                        the kit `Segmented` roots on a `div`, so the `nav` wraps it. */}
                    <nav aria-label={PRODUCT_FEATURE_PAGE_CHROME.tabsLabel}>
                        <Segmented
                            mode="links"
                            size="lg"
                            class="pf-tabs"
                            active={feature}
                            items={PRODUCT_FEATURE_TABS.map((tab) => ({ value: tab.slug, label: tab.label, href: tab.href }))}
                        />
                    </nav>

                    <div class="mk-hero">
                        <div class="wp-head--display mk-hero__copy">
                            <h1>{content.hero.title}</h1>
                            <p class="wp-lead">{content.hero.body}</p>
                            <PersonaPair
                                attribution={attribution}
                                investorTriggerId={investorTriggerId}
                                founderTriggerId={founderTriggerId}
                                page={trackingPage}
                                section="hero"
                                surface={surface}
                                labels={PRODUCT_FEATURE_PAGE_CHROME.personaCtas}
                                class="wp-acts mk-pair"
                            />
                        </div>
                        <HeroSampleCard sample={content.hero.mock} />
                    </div>
                </section>

                {afterHero ? (
                    <section class="mk-sec wrap pf-film" aria-label={PRODUCT_FEATURE_PAGE_CHROME.filmSectionLabel}>
                        {afterHero}
                    </section>
                ) : null}

                {/*---- - HOW ----*/}
                <section class="mk-sec wrap">
                    <SectionHead size="lg" title={content.how.title} text={content.how.intro} />
                    <Tray class="mk-cols3 ud-reading">
                        <GlossarySection>
                            {content.how.steps.map((step, index) => (
                                <MarketingCard
                                    key={step.title}
                                    top={
                                        <>
                                            <StepNumber>{stepNumber(index)}</StepNumber>
                                            <Tag label={step.tag} />
                                        </>
                                    }
                                    title={step.title}
                                    body={<MarketingGlossaryText>{step.body}</MarketingGlossaryText>}
                                />
                            ))}
                        </GlossarySection>
                    </Tray>
                </section>

                {/*---- - WHAT YOU GET ----*/}
                <section class="mk-sec wrap mk-split">
                    <SectionHead size="lg" class="mk-sticky" title={content.benefits.title} text={content.benefits.intro} />
                    <div class="mk-cols2">
                        <GlossarySection>
                            {content.benefits.items.map((item) => (
                                <MarketingCard
                                    key={item.title}
                                    as="div"
                                    class="mk-ben"
                                    // The card's monogram, its title's first letter; the phone sheet drops it.
                                    lead={<StepNumber tone="soft" class="mk-mono">{item.title.slice(0, 1)}</StepNumber>}
                                    title={item.title}
                                    body={<MarketingGlossaryText>{item.body}</MarketingGlossaryText>}
                                />
                            ))}
                        </GlossarySection>
                    </div>
                </section>

                {/*---- - GROUPS ----*/}
                <section class="mk-sec wrap">
                    <div class="mk-ghead">
                        <h2>{content.groups.title}</h2>
                        <p>{content.groups.note}</p>
                    </div>
                    <div class={`${groupColumnsClass(content.groups.items)} ud-reading`}>
                        {content.groups.items.map((group) => (
                            <MarketingCard key={group.title} as="div" title={group.title}>
                                <div class="mk-chips">
                                    {group.items.map((item) => (
                                        <Chip key={item} size="lg" label={item} />
                                    ))}
                                </div>
                            </MarketingCard>
                        ))}
                    </div>
                </section>

                {/*---- - NEXT ----*/}
                <section class="mk-sec wrap mk-split">
                    <SectionHead size="lg" class="mk-sticky" title={content.next.title} text={content.next.intro} />
                    <GlossarySection>
                        <StepList
                            steps={content.next.steps.map((step, index) => ({
                                key: step.title,
                                number: stepNumber(index),
                                title: step.title,
                                text: <MarketingGlossaryText>{step.body}</MarketingGlossaryText>,
                                // The last step is the call to act, its number in butter (what is due).
                                due: index === content.next.steps.length - 1,
                            }))}
                        />
                    </GlossarySection>
                </section>

                {/*---- - PLAN ----*/}
                <section class="mk-sec wrap mk-planrow">
                    <SectionHead size="lg" title={content.plan.title} text={content.plan.intro} />
                    <PlanCard
                        title={content.plan.card.name}
                        tag={content.plan.card.tagline}
                        price={planPrice}
                        period={content.plan.card.period}
                        checks={content.plan.card.bullets}
                        checkIcon={<Icon name="check" size={16} strokeWidth={2.4} />}
                        action={
                            <Button
                                variant="ink"
                                size="md"
                                block
                                href={pricingPlansHref}
                                onClick={() =>
                                    trackCta({ section: 'plan', label: content.plan.card.ctaLabel, kind: 'pricing', href: pricingPlansHref, triggerId: planTriggerId })
                                }
                            >
                                {content.plan.card.ctaLabel}
                            </Button>
                        }
                        note={content.plan.card.note}
                    />
                </section>

                <MarketingFaqSection title={PRODUCT_FEATURE_PAGE_CHROME.faqTitle(content.name)} items={content.faq} />

                <section class="mk-sec wrap">
                    <ClosingBand
                        title={content.finalCta.title}
                        action={{
                            label: content.finalCta.ctaLabel,
                            href: links.productDomains,
                            onClick: () => trackCta({ section: 'final_cta', label: content.finalCta.ctaLabel, kind: 'product', href: links.productDomains, triggerId: finalTriggerId }),
                        }}
                    />
                </section>

                <RelatedPages
                    class="wrap mk-sec"
                    label={PRODUCT_FEATURE_PAGE_CHROME.relatedLabel}
                    links={relatedLinks}
                    arrow={<Icon name="arrow-right" size={16} strokeWidth={2.2} />}
                />

                <section class="mk-sec wrap">
                    <WebsiteAskAi
                        title={MARKETING_ASK_AI_COPY.title}
                        context={askAiContext}
                        providerTitle={MARKETING_ASK_AI_COPY.providerTitle}
                    />
                    {/* Inside main, as the 17 other pages (web-frame-22). */}
                    <PageGeoBlock geo={geoContext} surface={geoSurface} />
                </section>
            </PageMain>

            <Footer links={links} sectionHrefPrefix="/" trackingPage={trackingPage} />
        </>
    );
};

export default ProductFeaturePage;
